import { push } from "connected-react-router";
import { useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";

import { QueryParams, UpdateQueryParams } from "../types";
import { getQueryStringValues, stringify } from "../utils";

export function useQueryStringValues<Values extends QueryParams>(defaultValues: Values): Values {
  const location = useLocation();

  return useMemo(() => {
    return getQueryStringValues(location.search, defaultValues);
  }, [location.search, defaultValues]);
}

export function useUpdateQueryStringValues<Values extends QueryParams>(
  defaultValues: Values,
): UpdateQueryParams<Values> {
  const dispatch = useDispatch();
  const location = useLocation();

  return useCallback<UpdateQueryParams<Values>>(
    (values: Values) => {
      const currentValues = getQueryStringValues(location.search, defaultValues);
      const newValues = { ...defaultValues, ...currentValues };

      const newKeys = Object.keys(values) as Array<keyof Values>;
      newKeys.forEach((key) => {
        newValues[key] = values[key];
      });

      const allKeys = Object.keys(defaultValues) as Array<keyof Values>;
      allKeys.forEach((key) => {
        if (Array.isArray(defaultValues[key])) {
          if (JSON.stringify(newValues[key]) === JSON.stringify(defaultValues[key])) {
            delete newValues[key];
          }
        } else if (newValues[key] === defaultValues[key]) {
          delete newValues[key];
        }
      });

      dispatch(
        push({
          pathname: location.pathname,
          hash: location.hash,
          search: stringify(newValues),
        }),
      );
    },
    [defaultValues, dispatch, location.hash, location.pathname, location.search],
  );
}
